import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  BookOpen,
  ClipboardList,
  Copy,
  GraduationCap,
  LineChart,
  Plus,
  Sparkles,
  Users,
  Wand2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  createClass,
  getAccountContext,
  listTeacherClasses,
  saveTeacherProfile,
  setAccountType,
  type ClassRow,
} from "@/lib/teacher.functions";

export const Route = createFileRoute("/_app/teacher")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Area professore — Lumen" },
      {
        name: "description",
        content:
          "Crea classi virtuali, condividi materiali e segui i progressi della classe con l'assistente AI di Lumen.",
      },
      { property: "og:title", content: "Area professore — Lumen" },
      { property: "og:description", content: "Classi virtuali, contenuti didattici e progressi della classe." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TeacherArea,
});

type TeacherProfile = {
  display_name?: string | null;
  subjects?: string[] | null;
  school_levels?: string[] | null;
  school?: string | null;
  years_experience?: number | null;
  languages?: string[] | null;
  class_types?: string[] | null;
  bio?: string | null;
  is_public?: boolean | null;
};

function csv(list?: string[] | null) {
  return (list ?? []).join(", ");
}
function parseCsv(value: string) {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 20);
}

function TeacherArea() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ctxFn = useServerFn(getAccountContext);
  const setRoleFn = useServerFn(setAccountType);
  const saveProfileFn = useServerFn(saveTeacherProfile);
  const listFn = useServerFn(listTeacherClasses);
  const createFn = useServerFn(createClass);

  const [loading, setLoading] = useState(true);
  const [isTeacher, setIsTeacher] = useState(false);
  const [profile, setProfile] = useState<TeacherProfile>({});
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", subject: "", school_level: "", school_year: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ctx = await ctxFn({ data: undefined } as never);
        if (!alive) return;
        const teacherRole = ctx.accountType === "teacher";
        setIsTeacher(teacherRole);
        setProfile((ctx.teacher as TeacherProfile) ?? { display_name: ctx.fullName });
        if (teacherRole) setClasses(await listFn({ data: undefined } as never));
      } catch {
        toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const becomeTeacher = async () => {
    await setRoleFn({ data: { accountType: "teacher" } });
    setIsTeacher(true);
    toast.success(t("teacher.role.saved"));
    setClasses(await listFn({ data: undefined } as never));
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await saveProfileFn({
        data: {
          display_name: profile.display_name ?? "",
          subjects: profile.subjects ?? [],
          school_levels: profile.school_levels ?? [],
          school: profile.school ?? null,
          years_experience: profile.years_experience ?? null,
          languages: profile.languages ?? [],
          class_types: profile.class_types ?? [],
          bio: profile.bio ?? null,
          is_public: !!profile.is_public,
        },
      });
      toast.success(t("teacher.profile.saved"));
    } catch {
      toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
    } finally {
      setSaving(false);
    }
  };

  const submitClass = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const row = await createFn({
        data: {
          name: form.name.trim(),
          subject: form.subject.trim(),
          school_level: form.school_level || null,
          school_year: form.school_year || null,
          description: form.description || null,
        },
      });
      setClasses((c) => [row, ...c]);
      setShowCreate(false);
      setForm({ name: "", subject: "", school_level: "", school_year: "", description: "" });
      toast.success(t("teacher.classes.created"));
      navigate({ to: "/class/$classId", params: { classId: row.id } });
    } catch {
      toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="px-4 py-6">
        <div className="h-32 animate-pulse rounded-3xl bg-muted/50" />
      </main>
    );
  }

  if (!isTeacher) {
    return (
      <main className="space-y-4 px-4 py-6">
        <h1 className="text-2xl font-bold tracking-tight">{t("teacher.dash.title")}</h1>
        <Card className="card-soft space-y-3 border-0 p-5">
          <p className="text-sm text-muted-foreground">{t("teacher.role.teacherDesc")}</p>
          <Button onClick={becomeTeacher} className="min-h-11 w-full">
            <GraduationCap className="mr-2 h-4 w-4" aria-hidden />
            {t("teacher.role.teacher")}
          </Button>
        </Card>
      </main>
    );
  }

  const tiles = [
    { key: "classes", icon: Users, tone: "bg-primary/10 text-primary" },
    { key: "create", icon: Wand2, tone: "bg-accent/15 text-accent-foreground", to: "/teaching-studio" },
    { key: "assigned", icon: ClipboardList, tone: "bg-secondary/40 text-foreground", to: "/teaching-studio" },
    { key: "results", icon: LineChart, tone: "bg-muted text-foreground", to: "/teacher-results" },
    { key: "contents", icon: BookOpen, tone: "bg-muted text-foreground", to: "/teaching-studio" },
    { key: "assistant", icon: Sparkles, tone: "bg-muted text-foreground", to: "/teacher-hub" },
  ] as const;

  return (
    <main className="space-y-5 px-4 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{t("teacher.dash.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("teacher.dash.subtitle")}</p>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          const to = "to" in tile ? tile.to : undefined;
          const inner = (
            <>
              <span className={`inline-flex h-9 w-9 items-center justify-center rounded-2xl ${tile.tone}`}>
                <Icon className="h-4.5 w-4.5" aria-hidden />
              </span>
              <span className="text-sm font-semibold leading-snug">{t(`teacher.dash.${tile.key}`)}</span>
              {!to && tile.key !== "classes" && (
                <span className="text-[11px] text-muted-foreground">{t("teacher.dash.soon")}</span>
              )}
            </>
          );
          return to ? (
            <Link key={tile.key} to={to} className="card-soft flex min-w-0 flex-col gap-2 rounded-3xl border-0 bg-card p-4 transition hover:brightness-[0.98]">
              {inner}
            </Link>
          ) : (
            <Card key={tile.key} className="card-soft flex min-w-0 flex-col gap-2 border-0 p-4">
              {inner}
            </Card>
          );
        })}
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t("teacher.dash.classes")}</h2>
          <Button size="sm" className="min-h-10" onClick={() => setShowCreate((v) => !v)}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            {t("teacher.classes.new")}
          </Button>
        </div>

        {showCreate && (
          <Card className="card-soft space-y-3 border-0 p-4">
            <h3 className="text-sm font-semibold">{t("teacher.classes.newTitle")}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="class-name">{t("teacher.classes.name")}</Label>
                <Input
                  id="class-name"
                  value={form.name}
                  placeholder={t("teacher.classes.namePh")}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="class-subject">{t("teacher.classes.subject")}</Label>
                <Input
                  id="class-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="class-level">{t("teacher.classes.level")}</Label>
                <Input
                  id="class-level"
                  value={form.school_level}
                  onChange={(e) => setForm({ ...form, school_level: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="class-year">{t("teacher.classes.year")}</Label>
                <Input
                  id="class-year"
                  value={form.school_year}
                  onChange={(e) => setForm({ ...form, school_year: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="class-desc">{t("teacher.classes.description")}</Label>
              <Textarea
                id="class-desc"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <Button className="min-h-11 w-full" disabled={saving || !form.name.trim()} onClick={submitClass}>
              {t("teacher.classes.create")}
            </Button>
          </Card>
        )}

        {classes.length === 0 ? (
          <Card className="card-soft border-0 p-5 text-sm text-muted-foreground">{t("teacher.dash.empty")}</Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {classes.map((c) => (
              <Card key={c.id} className="card-soft space-y-2 border-0 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-semibold leading-snug">{c.name}</p>
                    <p className="break-words text-xs text-muted-foreground">{c.subject}</p>
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {t("teacher.classes.members", { count: c.members ?? 0 })}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="rounded-lg bg-muted px-2 py-1 text-xs font-semibold tracking-widest">
                    {c.join_code}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-9"
                    onClick={() => {
                      navigator.clipboard?.writeText(c.join_code);
                      toast.success(t("teacher.classes.copied"));
                    }}
                  >
                    <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    {t("teacher.classes.copyCode")}
                  </Button>
                </div>
                <Button asChild size="sm" variant="secondary" className="min-h-10 w-full">
                  <Link to="/class/$classId" params={{ classId: c.id }}>
                    {t("teacher.classes.open")}
                  </Link>
                </Button>
              </Card>
            ))}
          </div>
        )}
        <p className="text-[11px] leading-relaxed text-muted-foreground">{t("teacher.privacy.note")}</p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t("teacher.profile.title")}</h2>
        <Card className="card-soft space-y-3 border-0 p-4">
          <p className="text-xs text-muted-foreground">{t("teacher.profile.subtitle")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="tp-name">{t("teacher.profile.name")}</Label>
              <Input
                id="tp-name"
                value={profile.display_name ?? ""}
                onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tp-subjects">{t("teacher.profile.subjects")}</Label>
              <Input
                id="tp-subjects"
                value={csv(profile.subjects)}
                placeholder={t("teacher.profile.subjectsHint")}
                onChange={(e) => setProfile({ ...profile, subjects: parseCsv(e.target.value) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tp-levels">{t("teacher.profile.levels")}</Label>
              <Input
                id="tp-levels"
                value={csv(profile.school_levels)}
                onChange={(e) => setProfile({ ...profile, school_levels: parseCsv(e.target.value) })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tp-school">{t("teacher.profile.school")}</Label>
              <Input
                id="tp-school"
                value={profile.school ?? ""}
                onChange={(e) => setProfile({ ...profile, school: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tp-years">{t("teacher.profile.years")}</Label>
              <Input
                id="tp-years"
                type="number"
                min={0}
                max={70}
                value={profile.years_experience ?? ""}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    years_experience: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tp-langs">{t("teacher.profile.languages")}</Label>
              <Input
                id="tp-langs"
                value={csv(profile.languages)}
                onChange={(e) => setProfile({ ...profile, languages: parseCsv(e.target.value) })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tp-bio">{t("teacher.profile.bio")}</Label>
            <Textarea
              id="tp-bio"
              rows={2}
              value={profile.bio ?? ""}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            />
          </div>
          <Button className="min-h-11 w-full" disabled={saving} onClick={saveProfile}>
            {t("teacher.profile.save")}
          </Button>
        </Card>
      </section>
    </main>
  );
}
